// An MCP client (any app or agent that signs in) sends a signed-out person to
// Poneva's authorization page. The server stores that pending request and
// redirects here with its opaque id. After sign-in the app returns to the one
// fixed, same-origin consent path; no caller-supplied URL is ever followed.
const KEY = 'poneva.oauthRequest';
const REQUEST_ID = /^mcpq_[A-Za-z0-9]{16,64}$/;
// Matches the server's pending-request lifetime; an abandoned request must not
// redirect a later, unrelated sign-in.
const MAX_AGE_MS = 10 * 60 * 1000;

type Stored = { id?: unknown; at?: unknown } | null;

function storedRequest(): string | null {
  let value: Stored;
  try {
    value = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as Stored;
  } catch {
    // Unreadable: treated as absent, so resuming still removes it.
    return null;
  }
  if (!value || typeof value.id !== 'string' || typeof value.at !== 'number') return null;
  if (!REQUEST_ID.test(value.id) || Date.now() - value.at > MAX_AGE_MS) return null;
  return value.id;
}

/** Keep a pending authorization request across sign-in; returns whether one exists. */
export function stashOAuthRequest(): boolean {
  let id: string | null = null;
  try {
    const params = new URLSearchParams(location.search);
    id = params.get('oauth_request');
    if (id !== null) {
      params.delete('oauth_request');
      const query = params.toString();
      history.replaceState(
        null,
        '',
        location.pathname + (query ? `?${query}` : '') + location.hash,
      );
    }
    if (id && REQUEST_ID.test(id))
      sessionStorage.setItem(KEY, JSON.stringify({ id, at: Date.now() }));
    return storedRequest() !== null;
  } catch {
    return false;
  }
}

/** After sign-in, continue to the stored consent request once. */
export function resumeOAuthRequest(): boolean {
  try {
    const id = storedRequest();
    sessionStorage.removeItem(KEY);
    if (!id) return false;
    location.assign(`/oauth/authorize?request=${encodeURIComponent(id)}`);
    return true;
  } catch {
    return false;
  }
}
